import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ParticleBackground } from "../components/ParticleBackground";
import { AlertTriangle } from 'lucide-react';
import { Button } from "../components/common/Button";
import { useAuthStore } from '../store';
import { PrepareForInterview } from '../components/dashboard/PrepareForInterview';
import { UpcomingInterviewsSidebar } from '../components/dashboard/UpcomingInterviewsSidebar';
import { SkillNewsCard } from '../components/dashboard/SkillNewsCard';
import { useNavigate, useLocation } from 'react-router-dom';
import InterviewForYouModal from '../components/dashboard/InterviewForYouModal';
import careerService from '../services/careerService';
import subscriptionService from '../services/subscriptionService';
import { RecommendedItem } from '../types/recommendations';
import interviewService, { UserInterview } from '../services/interviewService';
import userFeedbackService from '../services/userFeedbackService';
import FeedbackModal from '../components/FeedbackModal';
import { useToast } from '../components/common/Toast';
import SkillNewsService from '../services/skillNewsService';
import { CustomInterviewModal, CustomInterviewFormData } from '../components/interviews/CustomInterviewModal';
import { getPendingCustomInterview, clearPendingCustomInterview, hasPendingCustomInterview } from '../utils/customInterviewStorage';

const DashboardNew: React.FC = () => {
  const [timeOfDay, setTimeOfDay] = useState<string>('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [isTabletOrBelow, setIsTabletOrBelow] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [, setSubscriptionInfo] = useState<{
    planName: string;
    expiryDate: string;
    minutesLeft: number;
  }>({
    planName: 'Free Plan',
    expiryDate: '',
    minutesLeft: 0
  });
  const [, setIsLoadingSubscription] = useState(true);
  
  const { user } = useAuthStore();
  const username = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
  const navigate = useNavigate();
  const location = useLocation();
  const [, setUseStickyFallback] = useState(false);
  const { showToast } = useToast();
  // State for tracking if user has completed profile setup
  const [hasCompletedProfileSetup, setHasCompletedProfileSetup] = useState(true);
  const [isCheckingProfileSetup, setIsCheckingProfileSetup] = useState(true);
  
  // Curated interview data to show InterviewForYou Modal
  const [curatedInterview, setCuratedInterview] = useState<RecommendedItem | null>(null);

  // State for showing feedback modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // State for managing upcoming interviews from parent
  const [upcomingInterviews, setUpcomingInterviews] = useState<UserInterview[]>([]);
  const [isLoadingUpcomingInterviews, setIsLoadingUpcomingInterviews] = useState(true);

  // NEW: State to track if onboarding modal has been shown to prevent continuous callbacks
  const [hasShownOnboardingModal, setHasShownOnboardingModal] = useState(false);
  
  // Custom Interview Modal state - Shows from Extension custom interview intent
  const [isCustomInterviewModalOpen, setIsCustomInterviewModalOpen] = useState<boolean>(false);
  const [customInterviewData, setCustomInterviewData] = useState<Partial<CustomInterviewFormData> | undefined>(undefined);
  
  // Track if we've already attempted to prefetch skill news
  const hasPrefetchedSkillNews = useRef(false);

  // Function to fetch upcoming interviews from API
  const fetchUpcomingInterviews = useCallback(async () => {
    setIsLoadingUpcomingInterviews(true);
    try {
      // Get active interviews only (filter out inactive ones)
      const interviews = await interviewService.getUpcomingInterviews(true);
      // Limit to maximum 4 interviews for the sidebar
      setUpcomingInterviews(interviews.slice(0, 4));
      console.log('DashboardNew: upcomingInterviews', upcomingInterviews);
    } catch (err) {
      console.error('Error fetching upcoming interviews:', err);
      setUpcomingInterviews([]);
    } finally {
      setIsLoadingUpcomingInterviews(false);
    }
  }, []);

  // Function to trigger refresh of upcoming interviews (called when interviews are scheduled)
  const handleRefreshUpcomingInterviews = useCallback(() => {
    fetchUpcomingInterviews();
  }, [fetchUpcomingInterviews]);

  // Function to prefetch skill news in background
  const prefetchSkillNews = useCallback(async () => {
    // Check if we've already attempted to prefetch
    if (hasPrefetchedSkillNews.current) {
      console.log('DashboardNew: Skill news prefetch already attempted, skipping');
      return;
    }
    
    // Mark that we've attempted prefetching
    hasPrefetchedSkillNews.current = true;
    
    try {
      // Start async job with minimal logging
      await SkillNewsService.startAsyncNewsJob({
        skillCount: 5,
        newsPerSkill: 10,
        forceRegenerate: false // Respect cache, don't force regeneration
      });
      console.log('DashboardNew: Skill news prefetch job started successfully');
    } catch (error) {
      // Silently fail - this is a background operation
      console.debug('DashboardNew: Skill news prefetch failed (silent):', error);
    }
  }, []);

  // Load user subscription data
  useEffect(() => {
    const fetchUserSubscription = async () => {
      try {
        setIsLoadingSubscription(true);
        const info = await subscriptionService.getSubscriptionInfo();
        console.log('DashboardNew: Fetched subscription info:', info);
        setSubscriptionInfo({
          planName: info.planName,
          expiryDate: info.expiryDate,
          minutesLeft: info.minutesLeft
        });
      } catch (err) {
        console.error('DashboardNew: Error fetching subscription info:', err);
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    
    fetchUserSubscription();
  }, []);

  // Check if user has set up career preferences and resume
  useEffect(() => {
    const checkProfileSetup = async () => {
      try {
        setIsCheckingProfileSetup(true);
        
        // Check if user has at least one resume
        const resumes = await careerService.getResumes();
        const hasResume = resumes && resumes.length > 0;
        
        // Check if user has career preferences with required fields
        const careerPrefs = await careerService.getPreferences();
        const hasRequiredPrefs = careerPrefs && 
                               careerPrefs.experienceLevel && 
                               careerPrefs.preferredJobRoles.length > 0 &&
                               careerPrefs.interviewGoals.length > 0 &&
                               careerPrefs.languages.length > 0;
        
        console.log('DashboardNew: Profile setup check:', {
          hasResume,
          hasCareerPrefs: !!careerPrefs,
          hasRequiredPrefs
        });
        
        // User has completed setup if they have both resume and preferences
        const resumeAndPrefsComplete = !!(hasResume && hasRequiredPrefs);
        if (!resumeAndPrefsComplete) {
          console.log('DashboardNew: Profile setup is incomplete');
          // Check local storage for profile setup flag
          const hasCompletedProfileSetup = localStorage.getItem('hasCompletedProfileSetup');
          if (hasCompletedProfileSetup) {
            setHasCompletedProfileSetup(true);
          } else {
            setHasCompletedProfileSetup(false);
          }
        } else {
          console.log('DashboardNew: Profile setup is complete');
          // Profile setup is complete, set local storage flag
          localStorage.setItem('hasCompletedProfileSetup', 'true');
          setHasCompletedProfileSetup(true);
          
          // Prefetch skill news in background if user has resume
          if (hasResume) {
            console.log('DashboardNew: Prefetching skill news in background');
            prefetchSkillNews();
          }
        }
      } catch (error) {
        console.error('DashboardNew: Error checking profile setup:', error);
        // In case of error, assume setup is incomplete to prompt user
        setHasCompletedProfileSetup(false);
        // Check local storage for profile setup flag
        const hasCompletedProfileSetup = localStorage.getItem('hasCompletedProfileSetup');
        if (hasCompletedProfileSetup) {
          setHasCompletedProfileSetup(true);
        }
      } finally {
        setIsCheckingProfileSetup(false);
      }
    };
    
    // Only check if user is authenticated
    if (user && user._id) {
      checkProfileSetup();
    } else {
      setHasCompletedProfileSetup(true);
    }
  }, [user, prefetchSkillNews]);

  // Fetch upcoming interviews on component mount and when user changes
  useEffect(() => {
    if (user && user._id) {
      fetchUpcomingInterviews();
    }
  }, [user, fetchUpcomingInterviews]);

  // Check if user has completed interviews that need feedback
  useEffect(() => {
    const checkCompletedInterviewsForFeedback = async () => {
      // Only check if user is authenticated
      if (!user || !user._id) return;
      
      try {
        
        // Get completed interviews
        const completedInterviews = await interviewService.getUserInterviews('completed');
        
        if (completedInterviews.length > 0) {
          console.log('DashboardNew: User has completed interviews, checking for feedback');
          
            // Check if user has already submitted feedback for this interview
            const hasSubmittedFeedback = await userFeedbackService.hasFeedback();
            
            if (!hasSubmittedFeedback) {
              console.log(`DashboardNew: Feedback has not been submitted before`);
              setShowFeedbackModal(true);
            }
        }
      } catch (error) {
        console.error('DashboardNew: Error checking completed interviews for feedback:', error);
      }
    };
    
    // Delay the check to avoid multiple API calls at once during page load
    const timer = setTimeout(() => {
      checkCompletedInterviewsForFeedback();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [user]);

  // Add page load animation
  useEffect(() => {
    // Set document title
    document.title = 'Practice Dashboard | MockTrail - AI Interview Coach';
    
    // Short delay to ensure DOM is ready
    const animationTimer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);
    
    return () => clearTimeout(animationTimer);
  }, []);

  // Check mobile view with improved detection based on Navbar.tsx approach
  useEffect(() => {
    const checkViewportSize = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      const isTablet = width < 1280;
      setIsMobileView(isMobile);
      setIsTabletOrBelow(isTablet);
    };

    // Initialize on mount
    checkViewportSize();
    
    // Set up ResizeObserver for more reliable detection
    const bodyResizeObserver = new ResizeObserver(() => {
      // We observe the body to detect viewport size changes
      checkViewportSize();
    });
    
    bodyResizeObserver.observe(document.body);
    
    // Also listen for traditional events as fallback
    window.addEventListener('resize', checkViewportSize);
    
    // Force a re-check after a short delay to handle some edge cases
    setTimeout(checkViewportSize, 300);
    
    return () => {
      window.removeEventListener('resize', checkViewportSize);
      bodyResizeObserver.disconnect();
    };
  }, []);

  // Set time of day greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setTimeOfDay('Morning');
    } else if (hour < 17) {
      setTimeOfDay('Afternoon');
    } else {
      setTimeOfDay('Evening');
    }
  }, []);

  // Check for custom interview creation URL parameters
  useEffect(() => {
    if (location.search.includes('create-interview=true')) {
      const urlParams = new URLSearchParams(location.search);
      const createInterview = urlParams.get('create-interview');
      
      if (createInterview === 'true') {
        console.log('🔗 Custom interview creation detected in URL');
        
        // Parse URL parameters for interview data
        // Helper function to safely decode URI component
        const safeDecodeURIComponent = (str: string | null): string => {
          if (!str) return '';
          try {
            return decodeURIComponent(str);
          } catch (error) {
            console.warn('Failed to decode URI component:', str, error);
            // Return the original string if decoding fails
            return str;
          }
        };

        const interviewData: Partial<CustomInterviewFormData> = {
          jobRole: urlParams.get('jobRole') || '',
          companyName: urlParams.get('company') || '',
          interviewRoundType: urlParams.get('roundType') || '',
          jobDescription: safeDecodeURIComponent(urlParams.get('jobDescription')),
          interviewLanguage: urlParams.get('language') || 'English'
        };
        
        console.log('📝 Parsed interview data from URL:', interviewData);
        
        // Set the modal data and open it
        setCustomInterviewData(interviewData);
        setIsCustomInterviewModalOpen(true);
        
        // Clean up URL parameters by navigating to the same path without the create-interview params
        const cleanUrl = location.pathname;
        navigate(cleanUrl, { replace: true });
      }
    }
  }, [location.search, navigate]);
  
  // Check for pending custom interview parameters from localStorage (when user wasn't logged in)
  useEffect(() => {
    // Only check if user is authenticated
    if (!user || !user._id) {
      return;
    }
    
    // Check if there are pending custom interview params
    if (hasPendingCustomInterview()) {
      console.log('🔍 Found pending custom interview parameters in storage');
      
      const pendingParams = getPendingCustomInterview();
      
      if (pendingParams) {
        console.log('📝 Opening custom interview modal with stored params:', pendingParams);
        
        // Set the modal data and open it
        setCustomInterviewData(pendingParams);
        setIsCustomInterviewModalOpen(true);
        
        // Clear the stored parameters as they've been used
        clearPendingCustomInterview();
        
        // Show a toast to inform the user
        showToast('Opening custom interview creation from your previous request', 'info');
      }
    }
  }, [user, showToast]);

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  // Check if sticky position works, if not use fixed positioning
  useEffect(() => {
    const checkStickySupport = () => {
      // If we're on mobile, no need to check
      if (isTabletOrBelow) return;
      
      // Create a test element
      const testEl = document.createElement('div');
      testEl.style.position = 'sticky';
      testEl.style.top = '0';
      
      // Append to body, check computed style, then remove
      document.body.appendChild(testEl);
      const computedPosition = window.getComputedStyle(testEl).position;
      document.body.removeChild(testEl);
      
      // If position is not 'sticky', use fallback
      if (computedPosition !== 'sticky') {
        setUseStickyFallback(true);
      }
    };
    
    checkStickySupport();
  }, [isTabletOrBelow]);

  // Add page transition and sidebar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.98); }
        to { opacity: 1; transform: scale(1); }
      }
      
      @keyframes slideInFromRight {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      
      .dashboard-animate-in {
        opacity: 0;
        animation: fadeIn 0.5s ease-out forwards;
      }
      
      .dashboard-animate-in-delay-1 {
        opacity: 0;
        animation: fadeIn 0.6s ease-out 0.1s forwards;
      }
      
      .dashboard-animate-in-delay-2 {
        opacity: 0;
        animation: scaleIn 0.7s ease-out 0.2s forwards;
      }
      
      .dashboard-animate-in-delay-3 {
        opacity: 0;
        animation: slideInFromRight 0.8s ease-out 0.3s forwards;
      }
      
      /* Modal handling - pause all animations */
      body.modal-open [class*="dashboard-animate"],
      body.modal-was-open [class*="dashboard-animate"] {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
      
      /* Standard sidebar styles with no stickiness */
      .sidebar-component {
        margin-bottom: 24px;
      }
      
      /* Individual component scrolling */
      .sidebar-scrollable-component {
        max-height: 60vh;
        overflow-y: auto;
        padding-right: 0px;
        margin-bottom: 24px;
        scrollbar-width: thin;
        scrollbar-color: rgba(100, 116, 139, 0.4) transparent;
      }
      
      /* Hide scrollbar for Chrome, Safari and Opera */
      .sidebar-scrollable-component::-webkit-scrollbar {
        width: 4px;
      }
      
      .sidebar-scrollable-component::-webkit-scrollbar-track {
        background: transparent;
      }
      
      .sidebar-scrollable-component::-webkit-scrollbar-thumb {
        background-color: rgba(100, 116, 139, 0.4);
        border-radius: 20px;
      }
      
      /* Non-sticky styles for mobile and smaller screens */
      @media (max-width: 1279px) {
        .sidebar-scrollable-component {
          max-height: 60vh;
          overflow-y: auto;
          margin-bottom: 16px;
          scrollbar-width: thin;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // FIXED: Memoized handlers to prevent re-renders
  const handleStartInterview = useCallback((interviewId: string) => {
    console.log('DashboardNew: Starting interview:', interviewId);
    // Clear the localStorage flag
    localStorage.removeItem('from-onboarding');
    // Clear the previous path flag as well
    localStorage.removeItem('previous-path');
    // Reset the onboarding modal flag for future use
    setHasShownOnboardingModal(false);
    // Navigate to the interview preview page with the interview details
    navigate('/interview-preview/' + interviewId, { 
      state: { 
        interviewDetails: {
          // No userInterviewId here since not yet scheduled
          ...curatedInterview,
          id: interviewId,
          title: curatedInterview?.title || curatedInterview?.metadata.title,
          company: curatedInterview?.metadata.company,
          companyLogo: curatedInterview?.metadata.companyLogo || curatedInterview?.metadata.companyLogoUrl || curatedInterview?.companyLogo,
          companyId: curatedInterview?.metadata.companyId,
          jobRole: curatedInterview?.metadata.role,
          duration: curatedInterview?.metadata.durationMinutes,
          description: curatedInterview?.description || curatedInterview?.metadata.description,
          tags: curatedInterview?.metadata.tags,
          status: "not_started",
          language: curatedInterview?.metadata.language || 'English',
          voicePack: curatedInterview?.metadata.voicePack,
          characterGender: curatedInterview?.metadata.characterGender,
          interviewPrompt: curatedInterview?.metadata.interviewPrompt,
          series: undefined,
          createdAt: curatedInterview?.metadata.createdAt,
          updatedAt: curatedInterview?.metadata.updatedAt,
          newInterview: true
        } 
      }
    });
  }, [navigate, curatedInterview]);

  // FIXED: Memoized dismiss handler to prevent re-renders
  const handleDismissInterview = useCallback(() => {
    console.log('DashboardNew: User dismissed the interview modal');
    // Clear the localStorage flag
    localStorage.removeItem('from-onboarding');
    // Clear the previous path flag as well
    localStorage.removeItem('previous-path');
    // Reset the onboarding modal flag for future use
    setHasShownOnboardingModal(false);
  }, []);

  // FIXED: Memoized modal close handler to prevent re-renders
  const handleCloseInterviewModal = useCallback(() => {
    // Handle close modal if needed
  }, []);


  // Handle feedback submission
  const handleFeedbackSubmitted = async () => {
    setShowFeedbackModal(false);
    showToast('Thank you for your feedback!', 'success');
  };
  
  // Handle skip feedback
  const handleSkipFeedback = () => {
    setShowFeedbackModal(false);
  };

  // Handle custom interview modal close
  const handleCustomInterviewModalClose = () => {
    setIsCustomInterviewModalOpen(false);
    setCustomInterviewData(undefined);
  };

  // Handle custom interview form submission
  const handleCustomInterviewSubmit = async (formData: CustomInterviewFormData) => {
    try {
      console.log('🚀 Creating custom interview with data:', formData);
      
      // Map the modal's form data to the service's expected format
      const serviceFormData = {
        jobRole: formData.jobRole,
        company: formData.companyName, // Map companyName to company
        roundType: formData.interviewRoundType, // Map interviewRoundType to roundType
        customRoundType: formData.customRoundType,
        jobDescription: formData.jobDescription,
        language: formData.interviewLanguage, // Map interviewLanguage to language
        difficulty: formData.interviewDifficulty // Use the difficulty from the form
      };
      
      console.log('🔄 Mapped form data for service:', serviceFormData);
      
      // Call the interview service to create the custom interview
      const interview = await interviewService.createCustomInterview(serviceFormData);
      
      console.log('✅ Custom interview created:', interview);
      
      // Close the modal
      setIsCustomInterviewModalOpen(false);
      setCustomInterviewData(undefined);
      
      // Show success message
      showToast('Interview created successfully! You can find it in the Custom Interviews section.', 'success');
      
      // Refresh custom interviews and switch to practice tab
      if ((window as any).refreshCustomInterviewsAndSwitchTab) {
        console.log('🔄 Calling refresh function to update custom interviews tab');
        (window as any).refreshCustomInterviewsAndSwitchTab();
      }
      
    } catch (error) {
      console.error('❌ Error creating custom interview:', error);
      showToast(`Failed to create interview. ${(error as any).message[0]}`, 'error');
      throw error; // Re-throw to let the modal handle the error state
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <h1 className={`text-2xl md:text-3xl mt-6 font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2 ${isPageLoaded ? 'dashboard-animate-in' : 'opacity-0'}`}>
            Good {timeOfDay}, {username.charAt(0).toUpperCase() + username.slice(1)}
          </h1>
          <p className={`text-text-secondary-light dark:text-text-secondary-dark mb-8 max-w-3xl font-medium text-base ${isPageLoaded ? 'dashboard-animate-in-delay-1' : 'opacity-0'}`}>
          Practice recommended interviews, or customize your own, read news and continue your preparation
          </p>

          {/* Profile setup alert - Display if user hasn't completed setup */}
          {!isCheckingProfileSetup && !hasCompletedProfileSetup && (
            <div className={`p-4 rounded-xl border border-red-500/20 dark:border-red-400/20 bg-red-500/5 dark:bg-red-400/5 backdrop-blur-sm ${isPageLoaded ? 'dashboard-animate-in-delay-1' : 'opacity-0'}`}>
              <div className={`flex ${isMobileView ? 'flex-col' : 'items-center'}`}>
                <div className={`p-2 rounded-full bg-red-500/10 dark:bg-red-400/10 ${isMobileView ? 'mb-3 w-10 h-10' : 'mr-4'}`}>
                  <AlertTriangle className="w-6 h-6 text-red-500 dark:text-red-400" />
                </div>
                <div className={`flex-1 ${isMobileView ? 'mb-4' : ''}`}>
                  <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Profile Setup Incomplete
                  </h3>
                  <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                    Please set up your Resume & Career Preferences to get the most accurate interview experiences tailored to your profile.
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm"
                  className={`whitespace-nowrap ${isMobileView ? 'self-start' : 'ml-4'}`}
                  onClick={() => navigate('/settings-preferences?tab=preferences')}
                >
                  Complete Setup
                </Button>
              </div>
            </div>
          )}

          {/* Subscription Alert for Free Users - Updated with column-based layout on mobile */}
          {/* {userPlan === 'free' && (
            <div className={`mt-4 p-4 rounded-xl border border-amber-500/20 dark:border-amber-400/20 bg-amber-500/5 dark:bg-amber-400/5 backdrop-blur-sm ${isPageLoaded ? 'dashboard-animate-in-delay-1' : 'opacity-0'}`}>
              <div className={`flex ${isMobileView ? 'flex-col' : 'items-center'}`}>
                <div className={`p-2 rounded-full bg-amber-500/10 dark:bg-amber-400/10 ${isMobileView ? 'mb-3 w-10 h-10' : 'mr-4'}`}>
                  <AlertCircle className="w-6 h-6 text-amber-500 dark:text-amber-400" />
                </div>
                <div className={`flex-1 ${isMobileView ? 'mb-4' : ''}`}>
                  <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                    You're on the Free Plan
                  </h3>
                  <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                    Upgrade to get more interview minutes, unlimited custom interviews, premium skill news & more.
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm"
                  className={`whitespace-nowrap ${isMobileView ? 'self-start' : 'ml-4'}`}
                  onClick={() => navigate('/subscription')}
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          )} */}

            {/* Learn & Practice - Skill-Based News - Will be added back in next release*/} 
            {/* <div className={`mb-8 p-4 rounded-xl border border-purple-500/20 dark:border-purple-400/20 bg-purple-500/5 dark:bg-purple-400/5 backdrop-blur-sm ${isPageLoaded ? 'dashboard-animate-in-delay-1' : 'opacity-0'}`}>
              <div className={`flex ${isMobileView ? 'flex-col' : 'items-center'}`}>
                <div className={`p-2 rounded-full bg-purple-500/10 dark:bg-purple-400/10 ${isMobileView ? 'mb-3 w-10 h-10' : 'mr-4'}`}>
                  <Book className="w-6 h-6 text-purple-500 dark:text-purple-400" />
                </div>
                <div className={`flex-1 ${isMobileView ? 'mb-4' : ''}`}>
                  <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Stay ahead of the Curve <span className="ml-1 px-2 py-1 bg-purple-500/10 dark:bg-purple-400/10 border border-purple-500/20 dark:border-purple-400/20 rounded-full text-purple-500 dark:text-purple-400 font-medium text-xs">Learn & Practice - Skill-Based News</span>
                  </h3>
                  <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm max-w-3xl">
                    Get updated with the latest trends & updates from your domain, curated based on your professional profile. <br /> Learn new skills & practice them in interviews.
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm"
                  className={`whitespace-nowrap ${isMobileView ? 'self-start' : 'ml-4'}`}
                  onClick={() => navigate('/feed')}
                >
                  Start Reading
                </Button>
              </div>
            </div> */}

          {/* Main Content Grid */}
          <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Main Content Area - Left Side */}
            <div className="xl:col-span-2 space-y-8">
              {/* Prepare For Interview Component with Animation */}
              <div className={`${isPageLoaded ? 'dashboard-animate-in-delay-2' : 'opacity-0'}`}>
                <PrepareForInterview onRecommendationLoad={ (interview) => {
                  // Track if user came from onboarding/subscription page
                  const isFromOnboarding = localStorage.getItem('from-onboarding') === 'true';
                  const previousPath = localStorage.getItem('previous-path');
      
                  // FIXED: Only show modal if from onboarding AND hasn't been shown yet
                  if ((isFromOnboarding || previousPath?.includes('/onboarding/subscription')) && !hasShownOnboardingModal) {
                    console.log('DashboardNew: Showing onboarding modal for first time with interview:', interview);
                    setCuratedInterview(interview);
                    setHasShownOnboardingModal(true); // Prevent further callbacks
                  }
                }}
                onRefreshUpcomingInterviews={handleRefreshUpcomingInterviews}
                onRefreshCustomInterviews={() => {
                  // This callback will be used to refresh custom interviews and switch tabs
                  console.log('DashboardNew: Custom interview refresh callback provided');
                }}
                   />
              </div>

              {/* Role-based Job Interviews Component with Animation - Will be added back in next release*/}
                  {/* <div className={`${isPageLoaded ? 'dashboard-animate-in-delay-2' : 'opacity-0'}`}>
                    <RoleBasedInterviews />
                  </div> */}
            </div>

            {/* Sidebar - Right Side */}
            <div className={`xl:col-span-1 ${isPageLoaded ? 'dashboard-animate-in-delay-3' : 'opacity-0'}`}>
              <div className="mt-0">
                {/* <div className="sidebar-component">
                  <MustReadTips />
                </div> */}
                {!isMobileView && (
                <div className="sidebar-component">
                  <SkillNewsCard isMobileView={isMobileView} />
                </div>
                )}
                <div className="sidebar-component">
                  <UpcomingInterviewsSidebar 
                    upcomingInterviews={upcomingInterviews}
                    isLoading={isLoadingUpcomingInterviews}
                    onRefreshUpcomingInterviews={handleRefreshUpcomingInterviews}
                  />
                </div>
                {/* <div className="sidebar-component">
                  <AnimatedNewsForYou />
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Interview For You Modal - for new users, opens first time only */}
      <InterviewForYouModal
        isOpen={false} // Never showing it currently as recommendations are not correct due to lack of data.
        onClose={handleCloseInterviewModal}
        onStartInterview={handleStartInterview}
        onDismiss={handleDismissInterview}
        interview={curatedInterview as RecommendedItem}
      />
      
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onFeedbackSubmitted={handleFeedbackSubmitted}
        onSkip={handleSkipFeedback}
      />
      
      {/* Custom Interview Modal */}
      <CustomInterviewModal
        isOpen={isCustomInterviewModalOpen}
        onClose={handleCustomInterviewModalClose}
        onSubmit={handleCustomInterviewSubmit}
        initialData={customInterviewData}
      />
    </div>
  );
};

export default DashboardNew; 